import {
  AdminSpinner,
  Div,
  ReceivablesFilter,
  ReceivablesLocation,
  TableHeader,
  TabsWrapper,
} from '@vgrubs/components';
// Direct import: this one is deliberately outside the barrel.
import { NoDataComponent } from 'components/src/adminComponents/NoDataComponent/NoDataComponent';
import { useListScrollMemory } from '../hooks/useListScrollMemory';
import { useReceivables } from '../hooks/useReceivables';
import { FilterTab } from './FilterTab.styled';
import { ListFetchingOverlay } from './ListFetchingOverlay';
import { ReceivablesAgingCard } from './ReceivablesAgingCard';
import {
  RECEIVABLES_COLUMNS,
  ReceivablesLocationRow,
} from './ReceivablesLocationRow';
import { ReceivablesSummaryCards } from './ReceivablesSummaryCards';

type Props = {
  filter: ReceivablesFilter;
  onFilterChange: (filter: ReceivablesFilter) => void;
  /** Lower-cased search text, matched against location and owner names. */
  search: string;
  /** The page a row links to. */
  locationPath: (row: ReceivablesLocation) => string;
};

const FILTERS: { value: ReceivablesFilter; label: string }[] = [
  { value: 'all', label: 'All locations' },
  { value: 'owed', label: 'Owe something' },
  { value: 'reserveUsed', label: 'Used the reserve' },
];

const EMPTY: Record<ReceivablesFilter, { title: string; text: string }> = {
  all: { title: 'No locations', text: 'There are no locations to show.' },
  owed: {
    title: 'Nothing owed',
    text: 'No location owes vGrubs anything right now.',
  },
  reserveUsed: {
    title: 'Nobody on the reserve',
    text: 'No location is using the vDrive reserve right now.',
  },
};

/**
 * The "By Location" tab: the totals, the aging and one row per location.
 *
 * The list shows every location by default, the ones owing nothing last; the
 * filters narrow it to those that owe, or to those that used the vDrive
 * reserve (also one click on the "vDrive reserve used" card). The cards and
 * the aging are the same whatever the filter: they count what is owed.
 */
export const ReceivablesOverview = ({
  filter,
  onFilterChange,
  search,
  locationPath,
}: Props) => {
  const { receivables, isLoading, isFetching } = useReceivables(filter);
  const saveScroll = useListScrollMemory(
    'admin-receivables-scroll',
    !!receivables
  );

  if (isLoading || !receivables) {
    return isLoading ? <AdminSpinner /> : null;
  }

  const rows = search
    ? receivables.locations.filter(
        (row) =>
          row.locationName.toLowerCase().includes(search) ||
          row.ownerName.toLowerCase().includes(search)
      )
    : receivables.locations;

  return (
    <Div $flex $column $gap="16px">
      <ReceivablesSummaryCards
        summary={receivables.summary}
        onReserveUsed={() => onFilterChange('reserveUsed')}
      />
      <ReceivablesAgingCard aging={receivables.summary.aging} />

      {/* The wrapper's padding leaves room for the selected tab's shadow; the
          negative margin trims it. */}
      <Div $m="-8px 0 -24px" $mSM="-4px 0 -20px">
        <TabsWrapper>
          {FILTERS.map((item) => (
            <FilterTab
              key={item.value}
              $widthSM="calc((100% - 24px) / 3)"
              selected={filter === item.value}
              onClick={() => onFilterChange(item.value)}
            >
              {item.label}
            </FilterTab>
          ))}
        </TabsWrapper>
      </Div>

      {/* Any click that leaves the list starts here: it saves the spot Back
          returns to. */}
      <Div $relative onClickCapture={saveScroll}>
        {isFetching && <ListFetchingOverlay />}
        <TableHeader columns={RECEIVABLES_COLUMNS} gap="20px" />
        {rows.map((row) => (
          <ReceivablesLocationRow
            key={row.locationId}
            row={row}
            to={locationPath(row)}
          />
        ))}
        {rows.length === 0 && (
          <NoDataComponent
            height="30vh"
            heightSM="30vh"
            title={search ? 'No matches' : EMPTY[filter].title}
            description={
              search
                ? 'No location or owner with that name in this list.'
                : EMPTY[filter].text
            }
          />
        )}
      </Div>
    </Div>
  );
};
